import type * as lazyIndex from './lazyIndex';
import { LazyAction, LazyImport, LazyModule } from 'owa-bundling';

export { default as onAttachmentCreated } from './actions/onAttachmentCreated';
export { default as onAttachmentDeleted } from './actions/onAttachmentDeleted';
export type { default as AttachmentWellViewState } from './schema/AttachmentWellViewState';

export type {
    CreateAttachmentsProperties,
    ShareAttachmentProperties,
    ShouldShareAttachment,
} from './types/CreateAttachmentsProperties';

export type {
    OnAttachmentCreatedCallback,
    OnAttachmentCanceledOrFailedCallback,
} from './types/AttachmentUploadQueueItem';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "AttachmentsData"*/ './lazyIndex'),
    { name: 'AttachmentsData' }
);

// Delayed Loaded Actions
export const lazyInitializeAttachments: LazyAction<
    typeof lazyIndex.initializeAttachments,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.initializeAttachments);

export const lazyReinitializeAttachments: LazyAction<
    typeof lazyIndex.reinitializeAttachments,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.reinitializeAttachments);

export const lazyAddAttachmentIntoWellBeforeUpload: LazyAction<
    typeof lazyIndex.addAttachmentIntoWellBeforeUpload,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.addAttachmentIntoWellBeforeUpload);

// Delay Loaded utils
export const lazyCreateAttachmentsViaQueueManager: LazyImport<
    typeof lazyIndex.createAttachmentsViaQueueManager,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.createAttachmentsViaQueueManager);

export const lazycreateAttachmentsFromRecentAttachments: LazyImport<
    typeof lazyIndex.createAttachmentsFromRecentAttachments,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.createAttachmentsFromRecentAttachments);

export const lazyDeleteAttachmentsViaQueueManager: LazyAction<
    typeof lazyIndex.deleteAttachmentViaQueueManager,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.deleteAttachmentViaQueueManager);

export const lazyGetAttachmentWellInitialValue: LazyImport<
    typeof lazyIndex.getAttachmentWellInitialValue,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getAttachmentWellInitialValue);

export const lazyGetAttachmentWellForCompose: LazyImport<
    typeof lazyIndex.getAttachmentWellForCompose,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getAttachmentWellForCompose);

export const lazyGetAvailableLocalAttachmentSize: LazyImport<
    typeof lazyIndex.getAvailableLocalAttachmentSize,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getAvailableLocalAttachmentSize);

export const lazyConvertImageViewAttachmentToInlineAttachment: LazyAction<
    typeof lazyIndex.convertImageViewAttachmentToInlineAttachment,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.convertImageViewAttachmentToInlineAttachment);

export const lazyGetAllValidAttachments: LazyImport<
    typeof lazyIndex.getAllValidAttachments,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getAllValidAttachments);

export const lazyUploadLocalFile: LazyImport<typeof lazyIndex.uploadLocalFile, typeof lazyModule> =
    new LazyImport(lazyModule, m => m.uploadLocalFile);

export const lazyGetUpsellProperties: LazyImport<
    typeof lazyIndex.getUpsellProperties,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getUpsellProperties);

export const lazyUpdateAttachmentOffline: LazyImport<
    typeof lazyIndex.updateAttachmentOffline,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.updateAttachmentOffline);
export const lazyHandleAttachmentOfflineCreationError: LazyImport<
    typeof lazyIndex.handleAttachmentOfflineCreationError,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.handleAttachmentOfflineCreationError);
export const lazyHandleAttachmentOfflineDeleteFailure: LazyImport<
    typeof lazyIndex.handleAttachmentOfflineDeleteFailure,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.handleAttachmentOfflineDeleteFailure);
export const lazyDeleteAttachmentOffline: LazyImport<
    typeof lazyIndex.deleteAttachmentOffline,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.deleteAttachmentOffline);

// boot strap - used to load the package to make mutators work
export { lazyModule as preloadAttachmentsData };
